import { TextToggle } from '../../../../../../../components/ui-components/TextToggle'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumListEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import type { LumpsumAllocationCategory } from '../../../api/allocation.types'
import {
  ALLOCATION_MOVEMENT_TOGGLE_LABELS,
  ALLOCATION_MOVEMENT_TOGGLE_OPTIONS,
  AllocationMovementToggle,
  PlansActionFilter,
  PlansPlanMode
} from '../../../api/types'
import {
  isListPlansFetchFailed,
  isListPlansLoading,
  useLumpsumPlansContext
} from '../../LumpsumPlansProvider'
import { PlansCategoryListSkeleton } from '../../lumpsum-ui-components/PlansCategoryListSkeleton'
import { PlansListEmptyState } from '../../lumpsum-ui-components/PlansListEmptyState'
import { PlansListFilterEmptyState } from '../../lumpsum-ui-components/PlansListFilterEmptyState'
import { usePlanCategoryList } from '../../usePlanCategoryBucketExpansion'
import { AllocationCategoryCard } from './AllocationCategoryCard'
import { AllocationPlansListErrorState } from './AllocationPlansListErrorState'
import { isPlansListFullyEmpty, resolvePlanCategoryKey } from '../../../../utils/lumpsum/planListUtils'

type AllocationCategoryListProps = {
  /** When set, render these instead of context and skip query loading/error/empty shells. */
  categories?: LumpsumAllocationCategory[] | null
}

export function AllocationCategoryList({
  categories: dummyCategories
}: AllocationCategoryListProps = {}) {
  const {
    actionFiltersByPlanMode,
    allocationPlansData,
    allocationPlansQuery,
    allocationMovementToggle,
    handleAllocationMovementToggleChange
  } = useLumpsumPlansContext()
  const hasDummyCategories = dummyCategories !== undefined
  const allocationCategories = hasDummyCategories
    ? (dummyCategories ?? [])
    : allocationPlansData?.categories
  const actionFilter = hasDummyCategories
    ? PlansActionFilter.ALL
    : (actionFiltersByPlanMode[PlansPlanMode.ALLOCATION] ?? PlansActionFilter.ALL)

  const categoryEntries = usePlanCategoryList<LumpsumAllocationCategory>(
    allocationCategories,
    actionFilter,
    PlansPlanMode.ALLOCATION
  )

  if (!hasDummyCategories) {
    if (isListPlansLoading(PlansPlanMode.ALLOCATION, allocationPlansQuery)) {
      return <PlansCategoryListSkeleton />
    }

    if (isListPlansFetchFailed(PlansPlanMode.ALLOCATION, allocationPlansQuery)) {
      return <AllocationPlansListErrorState onRetry={allocationPlansQuery.refetch} />
    }

    if (isPlansListFullyEmpty(allocationCategories)) {
      return (
        <PlansListEmptyState
          planMode={PlansPlanMode.ALLOCATION}
          planSnapshotDate={allocationPlansData?.planSnapshotDate}
          planDurationInMonths={allocationPlansData?.planDurationInMonths}
        />
      )
    }

    if (categoryEntries.length <= 0) {
      return <PlansListFilterEmptyState actionFilter={actionFilter} />
    }
  }

  return (
    <>
      <AllocationCategoryListStickyBar
        allocationMovementToggle={allocationMovementToggle}
        onAllocationMovementToggleChange={handleAllocationMovementToggleChange}
      />
      {categoryEntries.map(({ category, categoryIndex }, entryIndex) => (
        <AllocationCategoryCard
          key={resolvePlanCategoryKey(category, categoryIndex)}
          category={category}
          categoryIndex={categoryIndex}
          actionFilter={actionFilter}
          noDivider={entryIndex === 0}
        />
      ))}
    </>
  )
}

type AllocationCategoryListStickyBarProps = {
  allocationMovementToggle: AllocationMovementToggle
  onAllocationMovementToggleChange: (toggle: AllocationMovementToggle) => void
}

export function AllocationCategoryListStickyBar({
  allocationMovementToggle,
  onAllocationMovementToggleChange
}: AllocationCategoryListStickyBarProps) {
  return (
    <div className="sticky top-[-8px] z-10 flex items-center justify-between bg-white px-5 pb-4 pt-5 border-b border-[#E8EBEA]">
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">Categories</div>
      <TextToggle
        currentToggleOption={ALLOCATION_MOVEMENT_TOGGLE_LABELS[allocationMovementToggle]}
        toggleOptions={ALLOCATION_MOVEMENT_TOGGLE_OPTIONS.map(
          (toggleOption) => ALLOCATION_MOVEMENT_TOGGLE_LABELS[toggleOption]
        )}
        onToggleClick={(toggleLabel) => {
          const nextToggle = ALLOCATION_MOVEMENT_TOGGLE_OPTIONS.find(
            (toggleOption) => ALLOCATION_MOVEMENT_TOGGLE_LABELS[toggleOption] === toggleLabel
          )
          if (nextToggle) {
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName: PlansScreen.LumpsumAllocation,
                eventId: PlansLumpsumListEventIds.MovementToggle,
                buttonLabel: nextToggle
              }
            })
            onAllocationMovementToggleChange(nextToggle)
          }
        }}
        toggleClassName="cursor-pointer text-[13px] font-[400] leading-[150%]"
        toggleColor="#3D3D3D"
      />
    </div>
  )
}
